import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
} from '@mui/material';
import {
  Close,
  DragIndicator,
  TrendingUp,
  PieChart,
  ScatterPlot,
  BarChart,
  ShowChart,
  RestartAlt,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export interface ChartConfig {
  id: string;
  name: string;
  enabled: boolean;
  description?: string;
}

export interface CostComplianceConfig {
  showSummaryCards: boolean;
  showDetailsTable: boolean;
  charts: ChartConfig[];
  compactMode: boolean;
}

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  config: CostComplianceConfig;
  onConfigChange: (config: CostComplianceConfig) => void;
  isDashboard?: boolean;
}

// Helper function to get icon for a chart
const getChartIcon = (chartId: string): React.ReactNode => {
  switch(chartId) {
    case 'dailyTrend':
      return <TrendingUp />;
    case 'productDistribution':
      return <PieChart />;
    case 'topContributors':
      return <BarChart />;
    case 'priorityMatrix':
      return <ScatterPlot />;
    case 'cumulativeLoss':
      return <ShowChart />;
    default:
      return <ShowChart />;
  }
};

const defaultCharts: ChartConfig[] = [
  {
    id: 'dailyTrend',
    name: 'Daily Loss Trend',
    enabled: true,
    description: 'Shows daily loss and gain trends over selected time period'
  },
  {
    id: 'productDistribution',
    name: 'Loss by Product',
    enabled: true,
    description: 'Pie chart showing loss distribution across products'
  },
  {
    id: 'topContributors',
    name: 'Top 10 Loss Contributors',
    enabled: true,
    description: 'Bar chart of meters with highest financial losses'
  },
  {
    id: 'priorityMatrix',
    name: 'Meter Priority Matrix',
    enabled: true,
    description: 'Scatter plot showing days out vs daily impact'
  },
  {
    id: 'cumulativeLoss',
    name: 'Cumulative Loss Tracking',
    enabled: true,
    description: 'Cumulative loss over time with projections'
  },
];

export const getDefaultConfig = (isDashboard: boolean = false): CostComplianceConfig => ({
  showSummaryCards: true,
  showDetailsTable: true,
  charts: [...defaultCharts],
  compactMode: isDashboard,
});

const CostOfNonComplianceSettings: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  config,
  onConfigChange,
  isDashboard = false,
}) => {
  const [localConfig, setLocalConfig] = useState<CostComplianceConfig>(config);

  const handleSave = () => {
    onConfigChange(localConfig);
    onClose();
  };

  const handleReset = () => {
    const defaultConfig = getDefaultConfig(isDashboard);
    setLocalConfig(defaultConfig);
  };

  const handleToggleChart = (chartId: string) => {
    setLocalConfig(prev => ({
      ...prev,
      charts: prev.charts.map(chart =>
        chart.id === chartId ? { ...chart, enabled: !chart.enabled } : chart
      ),
    }));
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(localConfig.charts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalConfig(prev => ({
      ...prev,
      charts: items,
    }));
  };

  const enabledCount = localConfig.charts.filter(c => c.enabled).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Dashboard Settings - Cost of Non-Compliance</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Basic Settings */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Display Options
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={localConfig.showSummaryCards}
                onChange={(e) => setLocalConfig(prev => ({
                  ...prev,
                  showSummaryCards: e.target.checked
                }))}
              />
            }
            label="Show Summary Cards"
          />
          <FormControlLabel
            control={
              <Switch
                checked={localConfig.showDetailsTable}
                onChange={(e) => setLocalConfig(prev => ({
                  ...prev,
                  showDetailsTable: e.target.checked
                }))}
              />
            }
            label="Show Details Table"
          />
          {isDashboard && (
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.compactMode}
                  onChange={(e) => setLocalConfig(prev => ({
                    ...prev,
                    compactMode: e.target.checked
                  }))}
                />
              }
              label="Compact Mode"
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Charts Configuration */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Charts & Analytics
            </Typography>
            <Chip
              label={`${enabledCount} of ${localConfig.charts.length} enabled`}
              size="small"
              color={enabledCount > 0 ? "primary" : "default"}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Drag to reorder • Toggle to show/hide • Configure which charts appear in the dashboard
          </Alert>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="charts">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                >
                  {localConfig.charts.map((chart, index) => (
                    <Draggable key={chart.id} draggableId={chart.id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            bgcolor: snapshot.isDragging ? 'action.hover' : 'transparent',
                            borderRadius: 1,
                            mb: 0.5,
                          }}
                        >
                          <ListItemIcon
                            {...provided.dragHandleProps}
                            sx={{ minWidth: 40, cursor: 'move' }}
                          >
                            <DragIndicator />
                          </ListItemIcon>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {getChartIcon(chart.id)}
                          </ListItemIcon>
                          <ListItemText
                            primary={chart.name}
                            secondary={chart.description}
                            sx={{
                              opacity: chart.enabled ? 1 : 0.5,
                            }}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              onChange={() => handleToggleChart(chart.id)}
                              checked={chart.enabled}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          startIcon={<RestartAlt />}
          onClick={handleReset}
          color="inherit"
        >
          Reset to Default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostOfNonComplianceSettings;